import React from "react";
import { View, Text, StyleSheet, Platform, Dimensions, TouchableOpacity, FlatList } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import LHFilter from "../../components/LHFilter";
import LHDataTable from "../../components/LHDataTable";
import ShiftHandOverCard, { Column } from "../../components/ShiftHandOverCard";
import { useTheme } from "../../theme";

interface ShiftHandOverMetricsProps {
  filterOptions: any;
  handleFilterChange: (key: string, value: any) => void;
  isLoading: boolean;
  apiError: string | null;
  handoverData: any[];
  tableColumns: Column[];
  handleRowClick: (rowData: any) => void;
  applyFilters?: () => Promise<void>;
  resetFilters?: () => void;
  showMobileFilterButton?: boolean;
  onMobileFilterPress?: () => void;
}

const ShiftHandOverMetrics: React.FC<ShiftHandOverMetricsProps> = ({
  filterOptions,
  handleFilterChange,
  isLoading,
  apiError,
  handoverData,
  tableColumns,
  handleRowClick,
  applyFilters,
  resetFilters,
  showMobileFilterButton = false,
  onMobileFilterPress,
}) => {
  const { resolvedTheme, colors } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  // Initialize localFilters from filterOptions selectedOptionId to show current selections
  const [localFilters, setLocalFilters] = React.useState<Record<string, any>>(() => {
    const initialFilters: Record<string, any> = {};
    filterOptions?.forEach((option: any) => {
      if (option.selectedOptionId) {
        const selectedOption = option.options.find((opt: any) => opt.id === option.selectedOptionId);
        if (selectedOption) {
          initialFilters[option.key] = selectedOption;
        }
      }
    });
    return initialFilters;
  });

  // Update localFilters when filterOptions change to reflect state changes
  React.useEffect(() => {
    const updatedFilters: Record<string, any> = {};
    filterOptions?.forEach((option: any) => {
      if (option.selectedOptionId) {
        const selectedOption = option.options.find((opt: any) => opt.id === option.selectedOptionId);
        if (selectedOption) {
          updatedFilters[option.key] = selectedOption;
        }
      }
    });
    setLocalFilters(updatedFilters);
  }, [filterOptions]);

  // Clear local filters when all filters are reset
  React.useEffect(() => {
    const hasAnySelection = filterOptions?.some((option: any) => option.selectedOptionId);
    if (!hasAnySelection) {
      setLocalFilters({});
    }
  }, [filterOptions]);
  const customizedColumns = React.useMemo(() => {
    const hasPeriodData = handoverData.some(
      (item) => "period" in item && item.period !== undefined
    );
    const hasWeekData = handoverData.some(
      (item) => "week" in item && item.week !== undefined
    );

    const hasPeriod = tableColumns.some((col) => col.columnKey === "period");
    const hasWeek = tableColumns.some((col) => col.columnKey === "week");
    const hasCombined = tableColumns.some(
      (col) => col.columnKey === "periodWeek"
    );
    const shouldCombine = hasPeriod && hasWeek && !hasCombined;

    let combinedCreated = false;
    let result = [...tableColumns];

    if (
      !hasPeriod &&
      !hasWeek &&
      !hasCombined &&
      (hasPeriodData || hasWeekData)
    ) {
      result = [
        ...result.slice(0, 1),
        {
          columnKey: "periodWeek",
          header: "Period Week",
          renderCell: (item: any) => (
            <Text style={styles.cellText}>{`${item.period || "-"} / ${item.week || "-"}`}</Text>
          ),
        },
        ...result.slice(1),
      ];
    }

    return result
      .map((column) => {
        if (column.columnKey === "details") {
          return {
            ...column,
            renderCell: (item: any) => (
              <Text
                style={[
                  styles.detailsCell,
                  isDarkMode && { color: colors.brand.primary },
                ]}
                onPress={() => handleRowClick({ ...item, columnKey: "details" })}
              >
                {item.details || "-"}
              </Text>
            ),
          };
        }

        if (column.columnKey === "periodWeek") {
          return {
            ...column,
            renderCell: (item: any) => (
              <Text style={[styles.cellText, isDarkMode && { color: colors.text.primary }]}>{`${item.period || "-"} / ${item.week || "-"}`}</Text>
            ),
          };
        }

        if (shouldCombine && column.columnKey === "period") {
          combinedCreated = true;
          return {
            ...column,
            columnKey: "periodWeek",
            header: "Period Week",
            renderCell: (item: any) => (
              <Text style={[styles.cellText, isDarkMode && { color: colors.text.primary }]}>{`${item.period || "-"} / ${item.week || "-"}`}</Text>
            ),
          };
        }

        if (shouldCombine && column.columnKey === "week" && combinedCreated)
          return null;

        return column;
      })
      .filter((col) => col !== null);
  }, [tableColumns, handoverData, handleRowClick, isDarkMode, colors.brand.primary, colors.text.primary]);

  const [currentPage, setCurrentPage] = React.useState(1);
  const rowsPerPage = 10;
  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return handoverData.slice(startIndex, startIndex + rowsPerPage);
  }, [currentPage, handoverData]);

  const totalPages = Math.ceil(handoverData.length / rowsPerPage);

  // Unified filter change handler: trigger API call immediately on selection
  const onFilterChange = (key: string, value: any) => {
    handleFilterChange(key, value);
  };

  const windowWidth = Dimensions.get("window").width;
  const isIOSMobile = Platform.OS === "ios" && windowWidth < 768;

  return (
    <View style={styles.metricsWrapper}>
      {/* Card Container with Filter and Table */}
      <View
        style={[
          styles.dataTableContainer,
          isDarkMode && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
            borderWidth: 1,
          },
          isIOSMobile && styles.dataTableContainerMobile,
        ]}
        testID="datatable-container"
      >
        {/* Header Box */}
        <View
          style={[
            styles.headerBox,
            isIOSMobile && styles.headerBoxMobileSpacing,
          ]}
        >
          {/* Mobile filter button - only show when prop is true */}
          {showMobileFilterButton && (
            <TouchableOpacity
              style={[
                styles.mobileFilterButton,
                isDarkMode && { backgroundColor: colors.brand.primary },
              ]}
              onPress={onMobileFilterPress}
            >
              <MaterialIcons name="filter-list" size={20} color="#FFFFFF" />
              <Text style={styles.mobileFilterButtonText}>Filter</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Filters Section - hide when mobile filter button is shown */}
        {!showMobileFilterButton && (
          <View style={styles.filterContainer} testID="filters-section">
            <LHFilter
              filters={filterOptions}
              onFilterChange={onFilterChange}
              itemsPerRow={getResponsiveItemsPerRow(6)}
              onClearAllFilters={resetFilters}
            />
          </View>
        )}

        {/* Show loading message or error if applicable */}
        {isLoading && (
          <View style={styles.messageContainer} testID="loading-container">
            <Text style={styles.loadingText} testID="loading-text">
              Loading shift handover data...
            </Text>
          </View>
        )}

        {apiError && (
          <View style={styles.messageContainer} testID="error-container">
            <Text style={styles.errorText} testID="error-text">
              Error: {apiError}
            </Text>
          </View>
        )}

        {/* Table Section */}
        {isIOSMobile ? (
          <>
            <FlatList
              data={isLoading ? [] : paginatedData}
              keyExtractor={(item, index) => item.ID?.toString() || index.toString()}
              renderItem={({ item }) => (
                <ShiftHandOverCard item={item} columns={customizedColumns} onRowClick={handleRowClick} />
              )}
              showsVerticalScrollIndicator={false}
            />
            <View style={styles.mobilePaginationContainer}>
              <View
                style={[
                  styles.mobilePaginationBox,
                  isDarkMode && { borderColor: colors.border.default },
                ]}
              >
                <TouchableOpacity
                  disabled={currentPage === 1}
                  onPress={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  style={[styles.mobilePaginationCell, styles.mobilePaginationArrowCell]}
                >
                  <MaterialIcons
                    name="chevron-left"
                    size={16}
                    color={
                      currentPage === 1
                        ? "#A0A0A0"
                        : isDarkMode
                        ? colors.text.primary
                        : "#005CBC"
                    }
                  />
                </TouchableOpacity>
                <TouchableOpacity
                  disabled={true}
                  style={styles.mobilePaginationCell}
                >
                  <Text
                    style={[
                      styles.mobilePaginationText,
                      { color: isDarkMode ? colors.text.primary : "#0066CC" },
                    ]}
                  >
                    {currentPage}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  disabled={currentPage >= totalPages}
                  onPress={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  style={[styles.mobilePaginationCell, styles.mobilePaginationArrowCell]}
                >
                  <MaterialIcons
                    name="chevron-right"
                    size={16}
                    color={
                      currentPage >= totalPages
                        ? "#A0A0A0"
                        : isDarkMode
                        ? colors.text.primary
                        : "#005CBC"
                    }
                  />
                </TouchableOpacity>
              </View>
            </View>
          </>
        ) : (
          <LHDataTable
            data={isLoading ? [] : paginatedData}
            columns={customizedColumns}
            tableHeader={""}
            tableDescription={
              isLoading ? "" : `Showing ${paginatedData.length} record(s)`
            }
            onRowClick={handleRowClick}
            highContrastText={true}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  metricsWrapper: {
    flex: 1,
  },
  messageContainer: {
    padding: 16,
    alignItems: "center",
  },
  headerBox: {
    backgroundColor: "transparent",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 8,
  },
  headerTitle: {
    fontFamily: "poppins-bold",
    fontWeight: "700",
    fontStyle: "normal",
    fontSize: 24,
    lineHeight: 28,
    letterSpacing: 0,
    color: "#3A3A3A",
    textAlign: "left",
  },
  headerBoxMobileSpacing: {
    marginBottom: 16,
  },
  filterContainer: {
    paddingBottom: 20,
    // paddingHorizontal: 8,
  },
  mobileFilterButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#0066CC",
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  mobileFilterButtonText: {
    color: "#FFFFFF",
    fontWeight: "600",
    marginLeft: 8,
  },
  loadingText: {
    fontSize: 16,
    color: "#0066CC",
  },
  errorText: {
    fontSize: 16,
    color: "#DC3545",
  },
  dataTableContainer: {
    ...Platform.select({
      ios: {
        shadowColor: "rgba(0, 94, 192, 0.08)",
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 24,
      },
      android: {
        elevation: 4,
        shadowColor: "rgba(0, 94, 192, 0.08)",
      },
      web: {
        boxShadow: "4px 0px 24px -2px rgba(0, 94, 192, 0.08)",
      },
    }),
    backgroundColor: "#fff",
    borderRadius: 8,
    marginTop: "1%",
    marginBottom: 20,
    paddingLeft: Dimensions.get("window").width < 768 ? 12 : 24,
    paddingTop: Dimensions.get("window").width < 768 ? 12 : 24,
    paddingRight: Dimensions.get("window").width < 768 ? 24 : 24,
  },
  dataTableContainerMobile: {
    backgroundColor: "transparent",
    borderRadius: 0,
    padding: 0,
    marginTop: 0,
    marginBottom: 0,
    paddingLeft: 0,
    paddingTop: 0,
    paddingRight: 0,
  },
  mobilePaginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 16,
    backgroundColor: "transparent",
  },
  mobilePaginationBox: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "transparent",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#D7E5F8",
    overflow: "hidden",
  },
  mobilePaginationCell: {
    minHeight: 32,
    minWidth: 32,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  mobilePaginationArrowCell: {
    backgroundColor: "transparent",
  },
  mobilePaginationText: {
    fontSize: 14,
    color: "#0066CC",
    fontWeight: "600",
  },
  paginationContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
  },
  paginationButton: {
    fontSize: 16,
    color: "#0066CC",
  },
  paginationText: {
    fontSize: 16,
    color: "#000",
  },
  cellText: {
    fontSize: 14,
    color: "#000",
  },
  detailsCell: {
    fontSize: 14,
    color: "#0066CC",
    textDecorationLine: "underline",
  },
});

export const getResponsiveItemsPerRow = (itemsPerRow: number): number => {
  const windowWidth = Dimensions.get("window").width;

  if (Platform.OS === "web") {
    return windowWidth > 1200
      ? Math.min(itemsPerRow, 6)
      : Math.min(itemsPerRow, 3);
  } else if (Platform.OS === "ios" || Platform.OS === "android") {
    if (windowWidth > 768) {
      return Math.min(itemsPerRow, 3);
    } else {
      return Math.min(itemsPerRow, 2);
    }
  }
  return itemsPerRow;
};

export default ShiftHandOverMetrics;
